{
  "title": "Autopilot",
  "lead": "An agent on the server sends metrics and the state of services and containers, and warns about problems. It only looks and warns: it changes nothing on the server.",
  "free": "Free",
  "agentOffline": "The agent is not connected: the server is off or the agent stopped. Reports are not available.",
  "unavailable": {
    "title": "Autopilot is unavailable",
    "text": "Autopilot is not set up on this CloudServer installation."
  },
  "what": {
    "metrics": "Metrics: CPU, memory, swap, load, disks, network, temperature — for an hour, a day, a week and a month.",
    "services": "systemd services and Docker containers: state, restarts, resources.",
    "warnings": "Warnings: package updates, certificates, full disks, failed services.",
    "safety": "The agent runs as the user beszel, opens no ports and leaves the firewall alone. It updates and restarts nothing: you run the fixing commands yourself."
  },
  "off": {
    "title": "Autopilot is off",
    "enable": "Turn on Autopilot",
    "guest": "A project owner or an administrator can turn on Autopilot."
  },
  "docker": {
    "label": "Watch Docker containers",
    "hint": "The agent joins the docker group. That is root-level access: Docker can control the whole server. The agent only reads the list and statistics of containers."
  },
  "status": {
    "waiting": "Waiting for the agent",
    "connected": "Agent connected",
    "reporting": "Reporting",
    "stale": "No reports",
    "expired": "Install key expired"
  },
  "statusHint": {
    "waiting": "The agent has not connected yet. A new server installs it by itself on first boot; on a running one, run the install command. The key works until {{time}}.",
    "connected": "The agent connected; the first report comes within a minute.",
    "stale": "Last report: {{time}}. The server is off, the agent stopped, or there is no connection.",
    "staleNever": "The agent has sent no report. The server is off, the agent stopped, or there is no connection.",
    "expired": "The agent never connected and the install key expired. Get a new command."
  },
  "health": {
    "ok": "All good",
    "warn": "Warnings",
    "critical": "Needs attention",
    "unknown": "No data"
  },
  "header": {
    "label": "Server state",
    "version": "Agent {{version}}",
    "lastSeen": "Report {{time}}",
    "uptime": "Up {{time}}",
    "cpu": "{{model}} · cores: {{cores}}",
    "memory": "Memory {{size}}"
  },
  "actions": {
    "command": "Install command",
    "disable": "Turn off Autopilot"
  },
  "enable": {
    "title": "Turn on Autopilot",
    "lead": "CloudServer gives you a command with a one-time key. Run it on the server as root: the agent installs itself and connects.",
    "reinstall": "A new command replaces the previous one, which stops working. You need it after reinstalling the server.",
    "submit": "Get the command",
    "commandTitle": "Install command",
    "commandLead": "Run it on the server as root. The command works once, until {{time}}.",
    "commandLabel": "Agent install command",
    "typeInConsole": "Type in the console",
    "consoleHint": "The server's console opens with this command; sign in there, then confirm typing it.",
    "consoleOff": "The console works while the server is on.",
    "done": "Done"
  },
  "disable": {
    "title": "Turn off Autopilot",
    "history": "This server's metrics history is deleted.",
    "agent": "The agent stays on the server and keeps trying to connect until you remove it with the command we show next.",
    "submit": "Turn off",
    "doneTitle": "Autopilot is off",
    "doneLead": "The agent is still on the server. Remove it with this command as root:",
    "commandLabel": "Agent removal command"
  },
  "copy": "Copy",
  "copied": "Copied",
  "warnings": {
    "title": "Warnings",
    "none": "No warnings.",
    "fix": "To check or fix (run it yourself):",
    "kinds": {
      "not_reporting": "The agent sends no reports",
      "memory_high": "Memory almost full: {{subject}}",
      "disk_full": "Disk almost full: {{subject}}",
      "service_failed": "Service failed: {{subject}}",
      "container_unhealthy": "Container unhealthy: {{subject}}",
      "container_stopped": "Container stopped with an error: {{subject}}",
      "container_restarting": "Container keeps restarting: {{subject}}",
      "updates_security": "Security updates: {{subject}}",
      "updates_pending": "Package updates available: {{subject}}",
      "certificate_expiring": "Certificate expires soon: {{subject}}",
      "certificate_expired": "Certificate expired: {{subject}}"
    },
    "severity": {
      "warn": "Warning",
      "critical": "Important"
    }
  },
  "charts": {
    "title": "Charts",
    "range": "Range",
    "ranges": {
      "1h": "1 hour",
      "24h": "24 hours",
      "7d": "7 days",
      "30d": "30 days"
    },
    "cpu": "CPU",
    "memory": "Memory",
    "swap": "Swap",
    "load": "Load average",
    "diskUsage": "Disk usage",
    "diskIo": "Disk reads and writes",
    "network": "Network",
    "temperatures": "Temperature",
    "empty": "No data for this range yet.",
    "celsius": "{{value}}\u00a0°C",
    "series": {
      "cpu": "Usage",
      "memory": "Used",
      "swap": "Used",
      "load1": "1\u00a0min",
      "load5": "5\u00a0min",
      "load15": "15\u00a0min",
      "read": "Read",
      "write": "Write",
      "in": "Inbound",
      "out": "Outbound"
    }
  },
  "containers": {
    "title": "Docker containers",
    "off": "Container monitoring is off. A new install command can turn it on.",
    "empty": "No containers.",
    "name": "Container",
    "image": "Image",
    "state": "State",
    "health": "Health check",
    "cpu": "CPU",
    "memory": "Memory",
    "restarts": "Restarts",
    "states": {
      "running": "Running",
      "restarting": "Restarting",
      "paused": "Paused",
      "created": "Created",
      "exited": "Stopped",
      "dead": "Dead",
      "unknown": "Unknown"
    },
    "healths": {
      "healthy": "Healthy",
      "unhealthy": "Unhealthy",
      "starting": "Starting",
      "none": "No check"
    }
  },
  "services": {
    "title": "systemd services",
    "empty": "The agent sent no list of services.",
    "name": "Service",
    "state": "State",
    "cpu": "CPU",
    "memory": "Memory",
    "restarts": "Restarts",
    "states": {
      "active": "Running",
      "inactive": "Not running",
      "failed": "Failed",
      "activating": "Starting",
      "deactivating": "Stopping",
      "reloading": "Reloading",
      "unknown": "Unknown"
    }
  },
  "updates": {
    "title": "Package updates",
    "unknown": "The agent has not checked for updates yet.",
    "none": "All packages are up to date.",
    "summary": "Updates available: {{total}}, security ones: {{security}}.",
    "checked": "Checked {{time}}.",
    "apply": "To install them, run this on the server as root. CloudServer never updates anything itself.",
    "commandLabel": "Package update command",
    "package": "Package",
    "current": "Installed",
    "available": "Available",
    "security": "Security"
  },
  "certificates": {
    "title": "TLS certificates",
    "none": "The agent sent no certificates to check.",
    "host": "Address",
    "expires": "Expires",
    "left": "{{time}}"
  },
  "ssh": {
    "title": "SSH logins",
    "unavailable": "This agent version does not collect SSH logins. They come with the security checks.",
    "empty": "No logins.",
    "note": "Visible to everyone with access to the project. Kept for 90 days.",
    "at": "When",
    "user": "User",
    "ip": "Address",
    "method": "Method",
    "result": "Result",
    "success": "Signed in",
    "failed": "Refused"
  },
  "unit": {
    "percent": "{{value}}\u00a0%"
  },
  "role": {
    "title": "Agent role",
    "lead": "What may be done on this server besides monitoring. The base is diagnostics (read-only); on top of it two independent capabilities, Autopilot's actions and app management, in any combination. Nobody gets a free shell: only the commands of CloudServer's catalogue and packages signed by us run.",
    "names": {
      "monitoring": "Monitoring only",
      "agent-ro": "Diagnostics, read-only",
      "agent-rw": "Diagnostics and Autopilot's actions",
      "agent-apps": "Diagnostics and app management",
      "agent-manage": "Diagnostics, actions and apps"
    },
    "short": {
      "monitoring": "monitoring",
      "agent-ro": "read-only diagnostics",
      "agent-rw": "diagnostics and actions",
      "agent-apps": "diagnostics and apps",
      "agent-manage": "actions and apps"
    },
    "hints": {
      "monitoring": "Only the Autopilot agent: metrics, services, containers and warnings. Nothing runs on the server on request.",
      "agent-ro": "Adds the user cloudserver-agent (no shell, no sudo; it may read the journal and logs) and a helper that runs read-only diagnostic commands from our catalogue: disks, memory, processes, ports, service status, the log of a unit or a container, allowed configuration files. Secrets are hidden from the output.",
      "agent-rw": "Diagnostics and Autopilot's actions: restarting services and containers, security updates, firewall ports, nginx, logs — as root, from the catalogue only and only after you confirm. No apps are installed.",
      "agent-apps": "Diagnostics and app management: the helper applies only packages signed by our key, writes only its own files named cs-… (a customer's file only through a card), and stops when they were changed by hand. No actions from the catalogue.",
      "agent-manage": "Diagnostics, Autopilot's actions and app management: everything both capabilities give. Servers with the former role «Package management» work this way."
    },
    "root": "Root-level commands, limited to CloudServer's catalogue or packages signed by us. Turn it on for servers where Autopilot should fix things or install apps after your confirmation.",
    "change": "Change role",
    "check": "Check role",
    "helper": "On the server: helper {{version}}, role «{{role}}», {{count}} commands · checked {{time}}",
    "helperNever": "Not checked yet. After the role command ran on the server, press «Check role».",
    "mismatch": "The server has the role «{{server}}», the panel «{{panel}}». Run the role command on the server.",
    "try": "Command to try",
    "commands": {
      "agent_info": "Helper information",
      "uptime": "Uptime and load",
      "df": "Disk usage",
      "free": "Memory",
      "systemctl_failed": "Failed services",
      "os_release": "System version"
    },
    "result": {
      "label": "Output of {{command}}",
      "exit": "Exit code {{code}}",
      "noExit": "No exit code",
      "duration": "{{ms}}\u00a0ms",
      "truncated": "The output was cut at 64\u00a0KB.",
      "redacted": "Secrets hidden from the output: {{count}}."
    },
    "dialog": {
      "title": "Agent role",
      "lead": "CloudServer saves your choice at once and gives a command that brings the server in line. Run it on the server as root.",
      "submit": "Save and get the command",
      "installTitle": "Command for the role",
      "installLead": "Run it on the server as root. It installs the helper and the user cloudserver-agent, trusts the organisation's SSH certificates for that user only, and for changes adds the sudoers rule (checked with visudo, removed again on error).",
      "removeTitle": "Command that removes the role's parts",
      "removeLead": "Run it on the server as root. The panel stops using the higher role right away.",
      "noneLead": "Nothing to do on the server: it already has only monitoring.",
      "commandLabel": "Role command"
    },
    "capabilityHints": {
      "actions": "Adds a sudoers rule letting cloudserver-agent run as root only the helper's write commands: restart, reload, start or stop a service or a container, install security updates or a package from the distribution, open or close a port in the firewall, check and reload nginx, rotate logs. Each runs only after you confirm it with a button. Nothing edits files, users, SSH settings or keys.",
      "apps": "Adds installing CloudServer apps: the helper applies only packages signed by our key (it checks the signature itself), writes only its own files named cs-…, and stops when they were changed by hand; a customer's file (such as /etc/docker/daemon.json) changes only through a card showing the exact edit. Every change is a revision you can roll back, and all of it goes into the change journal."
    },
    "levels": {
      "monitoring": "Monitoring only",
      "read": "Diagnostics (read-only)"
    },
    "capabilities": {
      "legend": "On top of diagnostics",
      "hint": "Each capability is turned on separately, in any combination.",
      "needsRead": "Choose «Diagnostics (read-only)» first.",
      "actions": "Autopilot's actions",
      "apps": "App management"
    }
  }
}
